import React, { useState } from 'react';
import { IUser } from '../Interfaces/IUser';
import { ILegalTransaction } from '../Interfaces/ILegalTransaction';
import { ILegalRequirement } from '../Interfaces/ILegalRequirement';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Checklist, FilterFrames, Group, Warning } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { ILegalTransactionProgress } from '../Interfaces/ILegalTransactionProgress';
import { IUserGroup } from '../Interfaces/IUserGroup';
import { CardRequirementsToCheck } from './Childs/CardRequirementsToCheck';
import { CardRequirementsExpired } from './Childs/CardRequirementsExpired';
import { CardKanbanLegalTransaction } from './Childs/CardKanbanLegalTransaction';

interface IProps {
  userArray: IUser[];
  userGroupArray: IUserGroup[];
  legalTransactionArray: ILegalTransaction[];
  legalTransactionProgressArray: ILegalTransactionProgress[];
}

function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name: string, ColorHex?: string) {
  return {
    sx: {
      bgcolor: ColorHex || stringToColor(name),
      width: 40,
      height: 40,
    },
    children:
      name.length === 0
        ? ''
        : name.split(' ').length === 1
          ? name[0]
          : `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
}

export const DashboardGlobal: React.FC<IProps> = ({
  userArray,
  userGroupArray,
  legalTransactionArray,
  legalTransactionProgressArray,
}) => {
  const navigation = useNavigate();

  const [toCheckNotary, setToCheckNotary] = useState([]);
  const [toCheckEmployeee, setToCheckEmployeee] = useState([]);
  const [toCheckUserGroup, setToCheckUserGroup] = useState([]);

  const getAvatarSizeByInitials = (len: number) => {
    if (len <= 2) {
      return { size: 40, fontSize: 16 };
    }
    if (len === 3) {
      return { size: 40, fontSize: 15 };
    }
    if (len === 4) {
      return { size: 42, fontSize: 12 };
    }
    return { size: 44, fontSize: 11 };
  };

  const getUserAvatarProps = (user: IUser) => {
    const initials = (user.Initials ?? '').trim();

    if (initials.length > 0) {
      const { size, fontSize } = getAvatarSizeByInitials(initials.length);
      return {
        sx: {
          bgcolor: user.ColorHex,
          width: size,
          height: size,
          fontSize: fontSize,
        },
        children: initials,
      };
    }
    return stringAvatar(`${user.FirstName} ${user.LastName}`, user.ColorHex);
  };

  const [toCheckDeadlineNotary, setToCheckDeadlineNotary] = useState([]);

  const [toCheckDeadlineEmployee, setToCheckDeadlineEmployee] = useState([]);

  const [toCheckDeadlineUserGroup, setToCheckDeadlineUserGroup] = useState([]);

  const spanTsx = (
    bodyState: string,
    borderProgress: string,
    txt: string | null = null,
    size: number = 25
  ) => {
    return (
      <>
        <span
          style={{
            padding: txt === null ? undefined : 5,
            backgroundColor: bodyState,
            borderStyle: 'solid',
            borderWidth: 5,
            borderColor: borderProgress,
            borderRadius: txt === null ? '50%' : 25,
            width: txt === null ? size : undefined,
            height: txt === null ? size : undefined,
            display: txt === null ? 'inline-block' : undefined,
          }}
        >
          {txt === null ? undefined : txt}
        </span>
      </>
    );
  };

  const instantViewLegalTransaction = (
    testLegalTransaction: ILegalTransaction
  ) => {
    let borderProgress = '#808080';
    let bodyState = '#c8c8c8 ';

    if (testLegalTransaction.idLegalTransactionState === 5) {
      bodyState = '#f2f299';
    } else if (testLegalTransaction.idLegalTransactionState === 10) {
      bodyState = '#c9d8f8';
    } else if (testLegalTransaction.idLegalTransactionState === 50) {
      bodyState = '#f5cfb4';
    } else if (testLegalTransaction.idLegalTransactionState === 100) {
      bodyState = '#99CC99';
    }

    if (testLegalTransaction.idLegalTransactionProgress === 20) {
      borderProgress = '#0f1b73';
    } else if (testLegalTransaction.idLegalTransactionProgress === 30) {
      borderProgress = '#FF2C2C';
    } else if (testLegalTransaction.idLegalTransactionProgress === 40) {
      borderProgress = '#008000';
    }

    return spanTsx(bodyState, borderProgress, testLegalTransaction.Title);
  };

  const genTableBodyTsx = () => {
    const getTsxForReq = (currentReq: ILegalRequirement, isNotary: boolean) => {
      const testLegalTransaction = legalTransactionArray.find(
        x => x.idLegalTransaction === currentReq.idLegalTransaction
      );
      const testUser = userArray.find(x =>
        isNotary
          ? currentReq.Notary_idUser === x.idUser
          : currentReq.Employee_idUser === x.idUser
      );
      let userGroup: undefined | IUserGroup = undefined;

      // PRüfen bei User === undefined, ob Usergroup
      if (testUser === undefined) {
        userGroup = userGroupArray.find(
          x => x.idUserGroup === currentReq.idUserGroup
        );
      }

      if (
        testLegalTransaction !== undefined &&
        (testUser !== undefined || userGroup !== undefined)
      ) {
        return (
          <Box
            sx={{ cursor: 'pointer' }}
            //onClick={() => navigation(`/legaltransaction/${testLegalTransaction!.idLegalTransaction}`)}
            onClick={() =>
              window.open(
                `/legaltransaction/${testLegalTransaction!.idLegalTransaction}`,
                '_blank'
              )
            }
          >
            {instantViewLegalTransaction(testLegalTransaction)}

            {testUser !== undefined ? (
              <Avatar
                {...getUserAvatarProps(testUser)}
                alt={`${testUser.FirstName} ${testUser.LastName}`}
              />
            ) : (
              <Box sx={{ display: 'flex' }}>
                <Avatar
                  {...stringAvatar(`${userGroup?.UserGroup}`)}
                  alt={`${userGroup?.UserGroup}`}
                />
                <Group sx={{ ml: -2, mt: 2, zIndex: 100 }} />
              </Box>
            )}
          </Box>
        );
      } else if (
        testLegalTransaction === undefined &&
        (testUser !== undefined || userGroup !== undefined)
      ) {
        return (
          <>
            {spanTsx(
              '#FF0000',
              '#FF0000',
              'Fehler: ' + String(currentReq.idLegalTransaction)
            )}
            {testUser !== undefined ? (
              <Avatar
                {...getUserAvatarProps(testUser)}
                alt={`${testUser.FirstName} ${testUser.LastName}`}
              />
            ) : (
              <>
                <Avatar
                  {...stringAvatar(`${userGroup?.UserGroup}`)}
                  alt={`${userGroup?.UserGroup}`}
                />
                <Group sx={{ ml: -2, mt: 2, zIndex: 100 }} />
              </>
            )}
          </>
        );
      } else if (
        testLegalTransaction !== undefined &&
        testUser === undefined &&
        userGroup === undefined
      ) {
        return (
          <Box
            sx={{ cursor: 'pointer' }}
            //onClick={() => navigation(`/legaltransaction/${testLegalTransaction!.idLegalTransaction}`)}
            onClick={() =>
              window.open(
                `/legaltransaction/${testLegalTransaction!.idLegalTransaction}`,
                '_blank'
              )
            }
          >
            {instantViewLegalTransaction(testLegalTransaction)}
            {spanTsx(
              '#FF0000',
              '#FF0000',
              'Fehler: ' + isNotary
                ? String(currentReq.Notary_idUser)
                : String(currentReq.Employee_idUser)
            )}
          </Box>
        );
      } else {
        return (
          <>
            {spanTsx(
              '#FF0000',
              '#FF0000',
              'Fehler: ' + String(currentReq.idLegalTransaction)
            )}
            {spanTsx(
              '#FF0000',
              '#FF0000',
              'Fehler: ' + isNotary
                ? String(currentReq.Notary_idUser)
                : String(currentReq.Employee_idUser)
            )}
          </>
        );
      }
    };

    let counter: number = 0;
    const returnTsx: React.ReactElement[] = [];

    while (
      counter < toCheckNotary.length ||
      counter < toCheckEmployeee.length ||
      counter < toCheckUserGroup.length
    ) {
      const currentNotaryReq =
        counter in toCheckNotary ? toCheckNotary[counter] : null;
      const currentUserGroupReq =
        counter in toCheckUserGroup ? toCheckUserGroup[counter] : null;
      const currentEmployeeReq =
        counter in toCheckEmployeee ? toCheckEmployeee[counter] : null;

      returnTsx.push(
        <TableRow>
          <TableCell>
            {currentNotaryReq === null ? (
              <></>
            ) : (
              getTsxForReq(currentNotaryReq, true)
            )}
          </TableCell>
          <TableCell>
            {currentUserGroupReq === null ? (
              <></>
            ) : (
              getTsxForReq(currentUserGroupReq, false)
            )}
            {currentEmployeeReq === null ? (
              <></>
            ) : (
              getTsxForReq(currentEmployeeReq, false)
            )}
          </TableCell>
        </TableRow>
      );

      counter++;
    }

    return returnTsx;
  };

  const genTableBodyDeadline = () => {
    const getTsxForReq = (currentReq: ILegalRequirement, isNotary: boolean) => {
      const testLegalTransaction = legalTransactionArray.find(
        x => x.idLegalTransaction === currentReq.idLegalTransaction
      );
      const testUser = userArray.find(x =>
        isNotary
          ? currentReq.Notary_idUser === x.idUser
          : currentReq.Employee_idUser === x.idUser
      );
      let userGroup: undefined | IUserGroup = undefined;

      // PRüfen bei User === undefined, ob Usergroup
      if (testUser === undefined) {
        userGroup = userGroupArray.find(
          x => x.idUserGroup === currentReq.idUserGroup
        );
      }

      if (
        testLegalTransaction !== undefined &&
        (testUser !== undefined || userGroup !== undefined)
      ) {
        return (
          <Box
            sx={{ cursor: 'pointer' }}
            //onClick={() => navigation(`/legaltransaction/${testLegalTransaction!.idLegalTransaction}`)}
            onClick={() =>
              window.open(
                `/legaltransaction/${testLegalTransaction!.idLegalTransaction}`,
                '_blank'
              )
            }
          >
            {instantViewLegalTransaction(testLegalTransaction)}

            {testUser !== undefined ? (
              <Avatar
                {...getUserAvatarProps(testUser)}
                alt={`${testUser.FirstName} ${testUser.LastName}`}
              />
            ) : (
              <Box sx={{ display: 'flex' }}>
                <Avatar
                  {...stringAvatar(`${userGroup?.UserGroup}`)}
                  alt={`${userGroup?.UserGroup}`}
                />
                <Group sx={{ ml: -2, mt: 2, zIndex: 100 }} />
              </Box>
            )}
          </Box>
        );
      } else if (
        testLegalTransaction === undefined &&
        (testUser !== undefined || userGroup !== undefined)
      ) {
        return (
          <>
            {spanTsx(
              '#FF0000',
              '#FF0000',
              'Fehler: ' + String(currentReq.idLegalTransaction)
            )}
            {testUser !== undefined ? (
              <Avatar
                {...getUserAvatarProps(testUser)}
                alt={`${testUser.FirstName} ${testUser.LastName}`}
              />
            ) : (
              <>
                <Avatar
                  {...stringAvatar(`${userGroup?.UserGroup}`)}
                  alt={`${userGroup?.UserGroup}`}
                />
                <Group sx={{ ml: -2, mt: 2, zIndex: 100 }} />
              </>
            )}
          </>
        );
      } else if (
        testLegalTransaction !== undefined &&
        testUser === undefined &&
        userGroup === undefined
      ) {
        return (
          <Box
            sx={{ cursor: 'pointer' }}
            //onClick={() => navigation(`/legaltransaction/${testLegalTransaction!.idLegalTransaction}`)}
            onClick={() =>
              window.open(
                `/legaltransaction/${testLegalTransaction!.idLegalTransaction}`,
                '_blank'
              )
            }
          >
            {instantViewLegalTransaction(testLegalTransaction)}
            {spanTsx(
              '#FF0000',
              '#FF0000',
              'Fehler: ' + isNotary
                ? String(currentReq.Notary_idUser)
                : String(currentReq.Employee_idUser)
            )}
          </Box>
        );
      } else {
        return (
          <>
            {spanTsx(
              '#FF0000',
              '#FF0000',
              'Fehler: ' + String(currentReq.idLegalTransaction)
            )}
            {spanTsx(
              '#FF0000',
              '#FF0000',
              'Fehler: ' + isNotary
                ? String(currentReq.Notary_idUser)
                : String(currentReq.Employee_idUser)
            )}
          </>
        );
      }
    };

    let counter: number = 0;
    const returnTsx: React.ReactElement[] = [];

    while (
      counter < toCheckNotary.length ||
      counter < toCheckEmployeee.length ||
      counter < toCheckUserGroup.length
    ) {
      const currentNotaryReq =
        counter in toCheckDeadlineNotary
          ? toCheckDeadlineNotary[counter]
          : null;
      const currentUserGroupReq =
        counter in toCheckDeadlineUserGroup
          ? toCheckDeadlineUserGroup[counter]
          : null;
      const currentEmployeeReq =
        counter in toCheckDeadlineEmployee
          ? toCheckDeadlineEmployee[counter]
          : null;

      returnTsx.push(
        <TableRow>
          <TableCell>
            {currentNotaryReq === null ? (
              <></>
            ) : (
              getTsxForReq(currentNotaryReq, true)
            )}
          </TableCell>
          <TableCell>
            {currentUserGroupReq === null ? (
              <></>
            ) : (
              getTsxForReq(currentUserGroupReq, false)
            )}
            {currentEmployeeReq === null ? (
              <></>
            ) : (
              getTsxForReq(currentEmployeeReq, false)
            )}
          </TableCell>
        </TableRow>
      );

      counter++;
    }

    return returnTsx;
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <CardRequirementsToCheck
            userArray={userArray}
            userGroupArray={userGroupArray}
          />
        </Grid>

        <Grid item xs={12}>
          <CardRequirementsExpired
            userArray={userArray}
            userGroupArray={userGroupArray}
          />
        </Grid>

        <Grid item xs={12}>
          <Card variant='outlined' sx={{ mt: 5 }}>
            <CardKanbanLegalTransaction
              userArray={userArray}
              userGroupArray={userGroupArray}
              legalTransactionProgressArray={legalTransactionProgressArray}
            />
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
